/** `/admin/tracks/:officialId/edit` — correct a circuit's map.
 *
 * What this screen can do is narrower than "edit the track", on purpose. It
 * DRAWS border records, which are evidence of their own kind and only ever
 * add. And it keeps CORRECTIONS beside the evidence: areas the map should not
 * draw, and this circuit's own answer about smoothing. It cannot delete a
 * surveyed record, and that is not caution for its own sake — a logger uploads
 * its whole bundle on every autosave, so a record deleted here would be back
 * the next time its owner drove the circuit. An area outlives that.
 *
 * Nothing on this screen changes the map. Submitting asks the merge job to
 * open a pull request in the track-data repository; what that would publish
 * arrives at Admin → Merge, set against what is published now; and a person
 * merges it. The screen says so wherever it could be mistaken for a save.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Notice } from '@/components/admin';
import { EditorCanvas, NO_SKETCH } from '@/components/editor/EditorCanvas';
import type { CommittedPath, EditorCanvasHandle, HoverInfo, SketchInfo, Tool } from '@/components/editor/EditorCanvas';
import {
  MAX_AREAS,
  MAX_NOTE,
  MAX_REASON,
  buildEdit,
  catchOf,
  countsOf,
  drawnSourceFor,
  excludedMask,
  initialState,
  isDirty,
  isDrawnOnly,
  nextAreaId,
  orderRuns,
  parseCorrections,
  perform,
  redo,
  restoreOp,
  sha256Hex,
  submitState,
  undo,
} from '@/components/editor/editSession';
import type { Corrections, EditState, ExclusionArea, LassoCatch } from '@/components/editor/editSession';
import { Card, Checkbox, ErrorNote, Kicker, Page, Segmented, Tag } from '@/components/ui';
import type { TagTone } from '@/components/ui';
import { LoadingPanel } from '@/components/ui/Spinner';
import {
  adminSettings,
  adminTrackSource,
  adminTrackSourceText,
  errorMessage,
  getTrack,
  submitTrackEdit,
} from '@/lib/api';
import { count, relativeTime } from '@/lib/format';
import { useMe } from '@/lib/session';
import type { Segment, TrackEditRow, TrackEditSource, TrackEditStatus, TrackEditSubmitted } from '@/lib/types';
import { parseBundle, pathToEdges, smoothRun } from '@/vendor/track-editor-core.mjs';
import type { Bundle, BundleEdge, ManualKind, PlanPoint, Side } from '@/vendor/track-editor-core.mjs';

const MUTED = 'var(--color-neutral-500)';
const SIDE_NAME: Record<Side, string> = { L: 'left', R: 'right' };

const TOOLS: ReadonlyArray<{ value: Tool; label: string; title: string }> = [
  { value: 'move', label: 'Move', title: 'Drag to move. The wheel zooms in every tool, and shift-drag moves.' },
  { value: 'exclude', label: 'Exclude', title: 'Drag a lasso round records the map should not draw.' },
  { value: 'draw', label: 'Draw', title: 'Click points along a border nobody drove. Enter adds it.' },
  { value: 'bridge', label: 'Bridge', title: 'Click the record either side of a gap to join them.' },
];

const HINT: Record<Tool, string> = {
  move: 'Drag to move and scroll to zoom. Hover a record to see what it is.',
  exclude:
    'Drag a lasso round what should not be there — a pit wall recorded as the border, a stray lap across the grass. Draw it tight: an area hides everything on its side inside it, drawn records included.',
  draw: 'Click along the border, a point every few metres and more on a bend. Backspace takes the last point back; Enter, or a double-click, adds the border.',
  bridge:
    'Click the last record before a gap, then the first one after it, on the same side. The join follows the headings those records were driven at.',
};

const STATUS: Record<TrackEditStatus, { label: string; tone: TagTone }> = {
  pending: { label: 'queued', tone: 'neutral' },
  pr_open: { label: 'pull request open', tone: 'accent' },
  applied: { label: 'on the map', tone: 'quiet' },
  closed: { label: 'closed unmerged', tone: 'outline' },
  stale: { label: 'stale', tone: 'warn' },
  failed: { label: 'refused', tone: 'danger' },
  superseded: { label: 'replaced', tone: 'outline' },
};

interface Loaded {
  source: TrackEditSource;
  bundle: Bundle;
  base: Corrections;
  baseDigest: string;
  gaps: Segment[];
  /** The service-wide switch, which "follow the service" follows. */
  serviceSmooths: boolean | null;
}

function smoothingWords(value: boolean | null, service: boolean | null): string {
  if (value === true) return 'always smoothed';
  if (value === false) return 'never smoothed';
  return service === null ? 'follows the service' : `follows the service (${service ? 'on' : 'off'} now)`;
}

function Row({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', ...style }}>{children}</div>;
}

export default function AdminTrackEditor() {
  const { officialId = '' } = useParams();
  const { me } = useMe();
  const canvas = useRef<EditorCanvasHandle | null>(null);

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const [state, setState] = useState<EditState | null>(null);
  const [tool, setTool] = useState<Tool>('move');
  const [sides, setSides] = useState<Record<Side, boolean>>({ L: true, R: true });
  const [drawSide, setDrawSide] = useState<Side>('R');
  const [drawKind, setDrawKind] = useState<ManualKind>('edge');
  const [tension, setTension] = useState(0.5);
  const [sketch, setSketch] = useState<SketchInfo>(NO_SKETCH);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const [activeArea, setActiveArea] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);

  // The lasso waiting on its dialog.
  const [caught, setCaught] = useState<LassoCatch | null>(null);
  const [areaSides, setAreaSides] = useState<Record<Side, boolean>>({ L: false, R: false });
  const [onlyDrawn, setOnlyDrawn] = useState(false);
  const [reason, setReason] = useState('');

  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<TrackEditSubmitted | null>(null);
  // Counts every change to the map, so "is this what was last submitted" is
  // two short strings to compare and not thousands of drawn records.
  const [revision, setRevision] = useState(0);
  const [submittedAs, setSubmittedAs] = useState<string | null>(null);

  // ── loading ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    setLoaded(null);
    setFailure(null);
    (async () => {
      const source = await adminTrackSource(officialId);
      const [bundleText, correctionsText, track, settings] = await Promise.all([
        adminTrackSourceText(officialId, 'bundle'),
        adminTrackSourceText(officialId, 'corrections'),
        // The published gaps are for finding what to bridge, and the switch is
        // for saying what "follow the service" means. Neither is worth
        // refusing to open the editor over.
        getTrack(officialId).catch(() => null),
        adminSettings().catch(() => null),
      ]);
      if (bundleText === null) throw new Error('The track data holds no survey of this circuit, so there is nothing to edit.');
      const bundle = parseBundle(bundleText);
      const base = parseCorrections(correctionsText, source.official_id, source.track_name);
      const baseDigest = await sha256Hex(correctionsText ?? '');
      const gapSpans = track?.geometry?.gaps;
      const smooths = settings?.settings.find((item) => item.key === 'compile.smooth_borders')?.value;
      if (!live) return;
      setLoaded({
        source,
        bundle,
        base,
        baseDigest,
        gaps: [...(gapSpans?.L ?? []), ...(gapSpans?.R ?? [])],
        serviceSmooths: typeof smooths === 'boolean' ? smooths : null,
      });
      setState(initialState(base));
      setSent(null);
      setSubmittedAs(null);
    })().catch((error: unknown) => {
      if (live) setFailure(error);
    });
    return () => {
      live = false;
    };
  }, [officialId, attempt]);

  const dirty = loaded && state ? isDirty(state, loaded.base) : false;

  // Leaving with work on the screen asks first. Nothing here is saved
  // anywhere until it is submitted.
  useEffect(() => {
    if (!dirty || sent) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, sent]);

  // ── derived ──────────────────────────────────────────────────────────────
  // What was surveyed, and what earlier edits drew in: one list, because on
  // the map they are one border. The drawn ones come from the corrections —
  // they are never in the bundle, which the datalogger has to be able to load.
  const edges = useMemo<BundleEdge[]>(
    () => (loaded ? [...loaded.bundle.edges, ...loaded.base.draw] : []),
    [loaded],
  );
  const mask = useMemo(() => excludedMask(edges, state?.areas ?? []), [edges, state?.areas]);
  const drawnMask = useMemo(() => excludedMask(state?.drawn ?? [], state?.areas ?? []), [state?.drawn, state?.areas]);
  const baseAreaIds = useMemo(() => new Set(loaded?.base.exclude.map((area) => area.id) ?? []), [loaded]);
  const counts = useMemo(
    () => (loaded && state ? countsOf(state, loaded.base, edges) : null),
    [loaded, state, edges],
  );
  const hiddenBy = useMemo(() => {
    const out = new Map<string, number>();
    for (const area of state?.areas ?? []) {
      out.set(area.id, excludedMask(edges, [area]).reduce((sum, flag) => sum + flag, 0));
    }
    return out;
  }, [edges, state?.areas]);

  const preview = useMemo(() => {
    if (!previewing || !state) return null;
    const raw: PlanPoint[][] = [];
    const smooth: PlanPoint[][] = [];
    for (const side of ['L', 'R'] as const) {
      if (!sides[side]) continue;
      const kept: BundleEdge[] = [
        ...edges.filter((edge, index) => edge.side === side && mask[index] !== 1),
        ...state.drawn.filter((edge, index) => edge.side === side && drawnMask[index] !== 1),
      ];
      for (const run of orderRuns(kept)) {
        const points = run.map((index) => ({ x: (kept[index] as BundleEdge).x, z: (kept[index] as BundleEdge).z }));
        raw.push(points);
        smooth.push(smoothRun(points));
      }
    }
    return { raw, smooth };
  }, [previewing, state, edges, mask, drawnMask, sides]);

  // ── doing things ─────────────────────────────────────────────────────────
  const act = useCallback((next: (current: EditState) => EditState) => {
    setState((current) => (current ? next(current) : current));
    setRevision((current) => current + 1);
    setSent(null);
  }, []);

  const onLasso = useCallback(
    (world: PlanPoint[]) => {
      if (!state) return;
      if (state.areas.length >= MAX_AREAS) {
        setSaid({ tone: 'warn', text: `A circuit holds at most ${MAX_AREAS} areas. Remove one first.` });
        return;
      }
      const found = catchOf(world, [...edges, ...state.drawn]);
      if (!found) return;
      if (found.inside.L + found.inside.R === 0) {
        setSaid({ tone: 'warn', text: 'That lasso went round no records, so there is nothing for an area there to keep out.' });
        return;
      }
      setSaid(null);
      setCaught(found);
      // Offered: the side most of what was caught is on. A lasso round a pit
      // wall catches the odd record of the other border too, and hiding those
      // by default would put a hole in the side nobody meant to touch.
      const mostlyLeft = found.inside.L > found.inside.R;
      setAreaSides({ L: mostlyLeft, R: !mostlyLeft });
      setOnlyDrawn(false);
      setReason('');
    },
    [state, edges],
  );

  const addArea = () => {
    if (!caught || !state) return;
    const chosen = (['L', 'R'] as const).filter((side) => areaSides[side]);
    if (chosen.length === 0 || !reason.trim()) return;
    const area: ExclusionArea = {
      id: nextAreaId([...(loaded?.base.exclude ?? []), ...state.areas]),
      sides: chosen,
      polygon: caught.polygon,
      y: caught.levels,
      only_drawn: onlyDrawn,
      reason: reason.trim(),
      by: me?.user.display_name ?? '',
      at: new Date().toISOString(),
    };
    act((current) => perform(current, { type: 'exclude', area }));
    setActiveArea(area.id);
    setCaught(null);
  };

  const onPath = useCallback(
    (committed: CommittedPath) => {
      if (!loaded || !state) return;
      const { source, run } = drawnSourceFor(loaded.bundle);
      const made = pathToEdges(committed.path, {
        side: committed.side,
        kind: committed.kind,
        source,
        run,
        existing: [...edges, ...state.drawn],
      });
      if (made.edges.length === 0) {
        setSaid({ tone: 'warn', text: 'Every metre of that path is already surveyed, so nothing was added: a drawn record never replaces a driven one.' });
        return;
      }
      act((current) => perform(current, { type: 'draw', edges: made.edges }));
      setSaid({
        tone: 'ok',
        text:
          `${count(made.edges.length)} m of ${SIDE_NAME[committed.side]} border drawn in` +
          (made.skipped > 0 ? `; ${count(made.skipped)} m skipped where the border is already surveyed.` : '.'),
      });
    },
    [loaded, state, act, edges],
  );

  const removeArea = (id: string) => {
    act((current) => {
      const op = restoreOp(current, id);
      return op ? perform(current, op) : current;
    });
    if (activeArea === id) setActiveArea(null);
  };

  const signature = `${revision}|${note.trim()}`;

  const submit = async () => {
    if (!loaded || !state || !dirty || !note.trim() || sending || signature === submittedAs) return;
    setSending(true);
    setSaid(null);
    try {
      const edit = buildEdit({
        officialId: loaded.source.official_id,
        bundle: loaded.bundle,
        base: loaded.base,
        baseDigest: loaded.baseDigest,
        state,
        note,
        now: new Date(),
      });
      const reply = await submitTrackEdit(loaded.source.official_id, edit);
      setSent(reply);
      setSubmittedAs(signature);
      setLoaded((current) =>
        current
          ? { ...current, source: { ...current.source, open_edit: reply.edit, edits: [reply.edit, ...current.source.edits] } }
          : current,
      );
    } catch (error) {
      setSaid({ tone: 'error', text: `The edit was not submitted: ${errorMessage(error)}` });
    } finally {
      setSending(false);
    }
  };

  // Undo and redo from the keyboard, wherever the focus is — except in a
  // field, where they belong to the text.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
      event.preventDefault();
      act(event.shiftKey ? redo : undo);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act]);

  // ── rendering ────────────────────────────────────────────────────────────
  if (failure) {
    return (
      <Page label="Track editor">
        <Link to="/admin/tracks">← Track editor</Link>
        <ErrorNote error={failure} title="The editor could not open this circuit" onRetry={() => setAttempt((n) => n + 1)} />
      </Page>
    );
  }
  if (!loaded || !state || !counts) {
    return (
      <Page label="Track editor">
        <LoadingPanel label="fetching the circuit’s evidence" />
      </Page>
    );
  }

  const { source } = loaded;
  const open = source.open_edit;
  // What is on the screen is what was last sent. Once it is not — the map
  // changed, or only the note did — the "submitted" notice is about something
  // else, and pressing the button would replace the edit it describes.
  const justSent = sent !== null && signature === submittedAs;
  const button = submitState({
    dirty,
    note,
    sending,
    signature,
    submittedAs,
    replaces: open !== null,
  });

  return (
    <Page label="Track editor" max={1500}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
          <Link to="/admin/tracks">← Track editor</Link>
          <Link to={`/tracks/${encodeURIComponent(source.official_id)}`}>{source.track_name}’s page</Link>
        </div>
        <Row style={{ justifyContent: 'space-between' }}>
          <h1 style={{ margin: 0, fontSize: 26 }}>Edit {source.track_name}</h1>
          <span className="tabular-nums" style={{ color: MUTED, fontSize: 13 }}>
            {source.official_id} · {count(loaded.bundle.edges.length)} surveyed
            {loaded.base.draw.length > 0 ? ` · ${count(loaded.base.draw.length)} drawn` : ''} · tracks/{source.slug}.json
          </span>
        </Row>
        <p style={{ margin: 0, color: MUTED, fontSize: 13.5, maxWidth: 900 }}>
          Nothing here deletes a surveyed record — its owner’s next upload would put it straight back. Excluding an area
          keeps what is inside it off the map, for good, and removing the area brings it back. Submitting opens a pull
          request; the map changes when a person merges it.
        </p>
      </div>

      {open && !justSent ? (
        <Notice tone="warn">
          An edit of this circuit is already {STATUS[open.status].label}
          {open.pr_url ? (
            <>
              {' '}
              (<a href={open.pr_url} target="_blank" rel="noreferrer noopener">its pull request</a>)
            </>
          ) : null}
          . It is not on the map yet, so it is not shown here — and submitting from this screen replaces it.
        </Notice>
      ) : null}

      <Row>
        <Segmented<Tool> label="Tool" options={TOOLS} value={tool} onChange={setTool} />
        <span style={{ flex: 1 }} />
        <Row style={{ gap: 4 }}>
          {(['L', 'R'] as const).map((side) => (
            <button
              key={side}
              type="button"
              role="switch"
              aria-checked={sides[side]}
              className="btn btn-ghost"
              style={{ fontSize: 12, padding: '3px 9px', opacity: sides[side] ? 1 : 0.5 }}
              onClick={() => setSides((current) => ({ ...current, [side]: !current[side] }))}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: side === 'L' ? '#9184d9' : 'oklch(0.78 0.10 225)',
                  opacity: sides[side] ? 1 : 0.3,
                }}
              />
              {SIDE_NAME[side]}
            </button>
          ))}
        </Row>
        <Row style={{ gap: 4 }}>
          <button type="button" className="btn btn-secondary" disabled={state.history.length === 0} onClick={() => act(undo)}>
            Undo
          </button>
          <button type="button" className="btn btn-secondary" disabled={state.future.length === 0} onClick={() => act(redo)}>
            Redo
          </button>
          <button type="button" className="btn btn-secondary" aria-label="Zoom in" onClick={() => canvas.current?.zoom(1)}>
            +
          </button>
          <button type="button" className="btn btn-secondary" aria-label="Zoom out" onClick={() => canvas.current?.zoom(-1)}>
            −
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => canvas.current?.fit()}>
            Fit
          </button>
        </Row>
      </Row>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <EditorCanvas
            ref={canvas}
            edges={edges}
            drawn={state.drawn}
            mask={mask}
            drawnMask={drawnMask}
            areas={state.areas}
            baseAreaIds={baseAreaIds}
            activeArea={activeArea}
            pendingArea={caught?.polygon ?? null}
            tool={tool}
            sides={sides}
            drawSide={drawSide}
            drawKind={drawKind}
            tension={tension}
            gaps={loaded.gaps}
            preview={preview}
            onLasso={onLasso}
            onPath={onPath}
            onSketch={setSketch}
            onHover={setHover}
            height="min(72vh, 760px)"
          />
          <div className="tabular-nums" style={{ fontSize: 12, color: MUTED, minHeight: 18 }}>
            {hover ? (
              <>
                x {hover.world.x.toFixed(1)} · z {hover.world.z.toFixed(1)}
                {hover.edge ? (
                  <>
                    {' · '}
                    {SIDE_NAME[hover.edge.side]} border, {hover.edge.kind}
                    {hover.drawnNow
                      ? ', drawn just now'
                      : isDrawnOnly(hover.edge)
                        ? ', drawn in an earlier edit'
                        : `, ${Object.values(hover.edge.votes).flatMap(Object.keys).length} vote(s)`}
                    {hover.edge.y !== null ? `, ${hover.edge.y.toFixed(1)} m up` : ''}
                    {hover.excluded ? ' — kept off the map by an area' : ''}
                  </>
                ) : null}
              </>
            ) : (
              'Amber dashes are the gaps in what is published now.'
            )}
          </div>
          {said ? (
            <Notice tone={said.tone} onDismiss={() => setSaid(null)}>
              {said.text}
            </Notice>
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card as="section" label="The tool in hand" style={{ gap: 10 }}>
            <Kicker>{TOOLS.find((item) => item.value === tool)?.label.toLowerCase()}</Kicker>
            <p style={{ margin: 0, fontSize: 13, color: MUTED }}>{HINT[tool]}</p>

            {tool === 'exclude' && caught ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13 }}>Keep off the map, inside this outline:</div>
                {(['L', 'R'] as const).map((side) => (
                  <Checkbox
                    key={side}
                    checked={areaSides[side]}
                    disabled={caught.inside[side] === 0}
                    onChange={(next) => setAreaSides((current) => ({ ...current, [side]: next }))}
                    label={`the ${SIDE_NAME[side]} border — ${count(caught.inside[side])} record${caught.inside[side] === 1 ? '' : 's'}`}
                  />
                ))}
                <Checkbox
                  checked={onlyDrawn}
                  onChange={setOnlyDrawn}
                  label="only records that were drawn"
                  description="Takes a drawn bridge back without hiding the real border when somebody surveys it."
                />
                <label style={{ fontSize: 12.5, color: MUTED }} htmlFor="area-reason">
                  Why — it goes in the pull request, and it is how somebody later judges whether to keep this
                </label>
                <textarea
                  id="area-reason"
                  className="input"
                  rows={2}
                  maxLength={MAX_REASON}
                  value={reason}
                  placeholder="The pit wall, recorded as the right-hand border"
                  onChange={(event) => setReason(event.target.value)}
                />
                <Row>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!reason.trim() || !(areaSides.L || areaSides.R)}
                    onClick={addArea}
                  >
                    Exclude this area
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setCaught(null)}>
                    Cancel
                  </button>
                </Row>
              </div>
            ) : null}

            {tool === 'draw' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Row>
                  <Segmented<Side>
                    label="Side to draw"
                    options={[
                      { value: 'L', label: 'Left border' },
                      { value: 'R', label: 'Right border' },
                    ]}
                    value={drawSide}
                    onChange={setDrawSide}
                  />
                </Row>
                <Row>
                  <Segmented<ManualKind>
                    label="What lies beyond it"
                    options={[
                      { value: 'edge', label: 'Edge' },
                      { value: 'wall', label: 'Wall' },
                      { value: 'runoff', label: 'Run-off' },
                    ]}
                    value={drawKind}
                    onChange={setDrawKind}
                  />
                </Row>
              </div>
            ) : null}

            {tool === 'bridge' ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: MUTED }}>
                How far the join follows the headings: {tension === 0 ? 'a straight line' : `${Math.round(tension * 100)} %`}
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={tension}
                  onChange={(event) => setTension(Number(event.target.value))}
                />
              </label>
            ) : null}

            {sketch.tool ? (
              <>
                <div className="tabular-nums" style={{ fontSize: 13 }}>
                  {sketch.tool === 'draw'
                    ? `${sketch.anchors} point${sketch.anchors === 1 ? '' : 's'}`
                    : `${sketch.anchors} of 2 records`}
                  {sketch.ready ? ` · ${sketch.metres.toFixed(0)} m` : ''}
                </div>
                {sketch.problem ? <div style={{ fontSize: 12.5, color: 'var(--color-warn)' }}>{sketch.problem}</div> : null}
                <Row>
                  <button type="button" className="btn btn-primary" disabled={!sketch.ready} onClick={() => canvas.current?.commit()}>
                    {sketch.tool === 'draw' ? 'Add this border' : 'Add this bridge'}
                  </button>
                  <button type="button" className="btn btn-ghost" disabled={sketch.anchors === 0} onClick={() => canvas.current?.cancel()}>
                    Cancel
                  </button>
                </Row>
              </>
            ) : null}
          </Card>

          <Card as="section" label="Excluded areas" style={{ gap: 8 }}>
            <Kicker>excluded areas · {state.areas.length}</Kicker>
            {state.areas.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: MUTED }}>None. Everything surveyed here is on the map.</p>
            ) : (
              state.areas.map((area) => (
                <div
                  key={area.id}
                  onMouseEnter={() => setActiveArea(area.id)}
                  onMouseLeave={() => setActiveArea((current) => (current === area.id ? null : current))}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    padding: '6px 8px',
                    borderRadius: 6,
                    background: activeArea === area.id ? 'color-mix(in srgb, var(--color-text) 6%, transparent)' : 'transparent',
                  }}
                >
                  <Row style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                    <span className="tabular-nums" style={{ fontSize: 13 }}>
                      {area.id} · {area.sides.map((side) => SIDE_NAME[side]).join(' and ')}
                      {area.only_drawn ? ', drawn only' : ''} · {count(hiddenBy.get(area.id) ?? 0)} hidden
                    </span>
                    {baseAreaIds.has(area.id) ? null : <Tag tone="accent">new</Tag>}
                  </Row>
                  <div style={{ fontSize: 12.5, color: MUTED }}>{area.reason}</div>
                  <Row style={{ gap: 4 }}>
                    <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => canvas.current?.lookAt(area.polygon)}>
                      Look
                    </button>
                    <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => removeArea(area.id)}>
                      Remove — bring its records back
                    </button>
                  </Row>
                </div>
              ))
            )}
          </Card>

          <Card as="section" label="Smoothing" style={{ gap: 8 }}>
            <Kicker>smoothing, for this circuit</Kicker>
            <Segmented<'follow' | 'on' | 'off'>
              label="Whether this circuit's borders are smoothed"
              options={[
                { value: 'follow', label: 'Follow the service' },
                { value: 'on', label: 'Always' },
                { value: 'off', label: 'Never' },
              ]}
              value={state.smooth === null ? 'follow' : state.smooth ? 'on' : 'off'}
              onChange={(next) => {
                const after = next === 'follow' ? null : next === 'on';
                if (after !== state.smooth) act((current) => perform(current, { type: 'smooth', before: current.smooth, after }));
              }}
            />
            <div style={{ fontSize: 12.5, color: MUTED }}>
              Now: {smoothingWords(state.smooth, loaded.serviceSmooths)}. The switch for every circuit is in Admin →
              Settings; this is this circuit’s own answer over it.
            </div>
            <Checkbox
              checked={previewing}
              onChange={setPreviewing}
              label="Show what smoothing does here"
              description="The compiler’s own smoother, run in this page on the records as they stand: faint is as recorded, bright is smoothed. The order is a rough walk, not the compiler’s, so trust the shape and not a wrong turn at a fork."
            />
          </Card>

          <Card as="section" label="Submit" style={{ gap: 8 }}>
            <Kicker>this edit</Kicker>
            {dirty ? (
              <ul className="tabular-nums" style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {counts.drawn_m > 0 ? <li>{count(counts.drawn_m)} m of border drawn in</li> : null}
                {counts.areas_added > 0 ? <li>{counts.areas_added} area{counts.areas_added === 1 ? '' : 's'} excluded</li> : null}
                {counts.areas_removed > 0 ? <li>{counts.areas_removed} area{counts.areas_removed === 1 ? '' : 's'} removed</li> : null}
                {counts.hidden !== counts.hidden_before ? (
                  <li>
                    {count(counts.hidden)} surveyed records kept off the map (was {count(counts.hidden_before)})
                  </li>
                ) : null}
                {counts.smoothing_changed ? <li>smoothing: {smoothingWords(state.smooth, loaded.serviceSmooths)}</li> : null}
              </ul>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: MUTED }}>Nothing changed yet.</p>
            )}
            <label style={{ fontSize: 12.5, color: MUTED }} htmlFor="edit-note">
              What this edit is for — the pull request’s first line
            </label>
            <textarea
              id="edit-note"
              className="input"
              rows={3}
              maxLength={MAX_NOTE}
              value={note}
              placeholder="Keep the pit wall out of the right-hand border, and bridge the gap after it."
              onChange={(event) => setNote(event.target.value)}
            />
            <button type="button" className="btn btn-primary btn-block" disabled={!button.enabled} onClick={submit}>
              {button.label}
            </button>
            {button.why && dirty ? <div style={{ fontSize: 12.5, color: MUTED }}>{button.why}</div> : null}
            {justSent && sent.duplicate ? (
              <Notice tone="neutral">
                This is the edit that is already {STATUS[sent.edit.status].label}
                {sent.edit.pr_url ? (
                  <>
                    {' '}
                    (<a href={sent.edit.pr_url} target="_blank" rel="noreferrer noopener">its pull request</a>)
                  </>
                ) : null}
                , so nothing new was submitted.
              </Notice>
            ) : justSent ? (
              <Notice tone={sent.dispatched ? 'ok' : 'warn'}>
                Submitted as {sent.edit.id}. {sent.dispatched ? 'The merge job has been asked to run for this circuit: a pull request follows in a minute or two' : `The merge job was not started from here (${sent.reason}), so the pull request follows at its next run`}
                , and what it would publish will be at <Link to="/admin/merge">Admin → Merge</Link> behind its <b>map</b> button. Nothing is on the map until that pull request is merged.
              </Notice>
            ) : null}
          </Card>

          {source.edits.length > 0 ? (
            <Card as="section" label="Earlier edits" style={{ gap: 6 }}>
              <Kicker>edits of this circuit</Kicker>
              {source.edits.map((row: TrackEditRow) => (
                <div key={row.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12.5 }}>
                  <Row style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                    <span className="truncate" title={row.note}>
                      {row.note}
                    </span>
                    <Tag tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Tag>
                  </Row>
                  <span style={{ color: MUTED }}>
                    {row.created_by || 'an administrator'} · {relativeTime(row.created_at)}
                    {row.pr_url ? (
                      <>
                        {' · '}
                        <a href={row.pr_url} target="_blank" rel="noreferrer noopener">
                          pull request
                        </a>
                      </>
                    ) : null}
                    {row.reason ? ` · ${row.reason}` : ''}
                  </span>
                </div>
              ))}
            </Card>
          ) : null}
        </div>
      </div>
    </Page>
  );
}
